import { memoriesOf } from 'helpers/memories';
import {
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Album } from 'models/album';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { VaultSearchBar } from 'common/vault-search-bar';
import { AlbumThumbnail } from 'common/album-thumbnail';
import { ContentIcons } from 'assets/icons/content-icons';
import { VaultNavigationTabs } from 'common/enums';
import { ClockIcon, ColumnsIcon, GridIcon } from 'assets/icons';
import {
  adaptContentType,
  classnames,
  excludeDefaultAlbum,
  getAlbumRepresentativeIcon,
} from 'helpers/utils';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { goToPage, RouteName } from 'routes';
import { MemoryCard } from '../dashboard/components/memory-card';
import styles from './timeline.module.scss';

type ZoomLevel = 'year' | 'month' | 'week';
type ViewMode = 'albums' | 'memories';

type DatedItem = {
  album: Album;
  isAnswered: boolean;
  start: Date;
  end: Date;
  entries: { content: AlbumContentSerialized; date: Date }[];
};

const parseDateSafe = (value?: string | null): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map((p) => p.trim());
  const year = parseInt(y, 10);
  if (Number.isNaN(year)) return null;
  const month = m ? parseInt(m, 10) - 1 : 0;
  const day = d ? parseInt(d, 10) : 1;
  return new Date(year, month, day);
};

const formatShortDate = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// The dashboard memory card's date style ("June 3, 2019").
const formatLongDate = (d: Date) =>
  d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const startOfWeek = (d: Date): Date => {
  const day = d.getDay();
  const diffToMonday = (day + 6) % 7;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  monday.setDate(monday.getDate() - diffToMonday);
  return monday;
};

type PeriodMeta = {
  key: string;
  label: string;
  groupLabel?: string; // year for month/week views
  date: Date; // canonical start of period
};

const periodMeta = (date: Date, zoom: ZoomLevel): PeriodMeta => {
  if (zoom === 'year') {
    const year = date.getFullYear();
    return {
      key: String(year),
      label: String(year),
      date: new Date(year, 0, 1),
    };
  }
  if (zoom === 'month') {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      key: `${year}-${String(month).padStart(2, '0')}`,
      label: new Date(year, month, 1).toLocaleDateString('en-US', {
        month: 'short',
      }),
      groupLabel: String(year),
      date: new Date(year, month, 1),
    };
  }
  // week
  const weekStart = startOfWeek(date);
  return {
    key: `w-${weekStart.getFullYear()}-${String(weekStart.getMonth()).padStart(
      2,
      '0',
    )}-${String(weekStart.getDate()).padStart(2, '0')}`,
    label: weekStart.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    groupLabel: String(weekStart.getFullYear()),
    date: weekStart,
  };
};

// Columns share the timeline's full width. With more periods than fit, they
// keep this minimum (room for an album card's title and entry count) and the
// timeline scrolls sideways.
const MIN_COLUMN_WIDTH: Record<ZoomLevel, number> = {
  year: 300,
  month: 280,
  week: 240,
};

// Mounts its children once they're near the screen. A vault can hold
// thousands of memories and every card loads a thumbnail (a video's reads the
// file itself), so cards further away wait as same-size placeholders.
const WhenNearView = ({
  children,
  placeholderClassName,
}: {
  children: ReactNode;
  placeholderClassName: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);
  return (
    <div ref={ref} className={near ? undefined : placeholderClassName}>
      {near ? children : null}
    </div>
  );
};

// Space between a card and the edges of the columns it spans.
const CARD_INSET = 6;
// An album card's padding plus border, left and right together.
const CARD_CHROME = 34;
// Entry chips inside an album card: the narrowest a chip gets, the gap
// between chips, and how many rows show before the rest fold into "+N more".
const CHIP_MIN_WIDTH = 150;
const CHIP_GAP = 6;
const CHIP_ROWS = 3;

export const Timeline = () => {
  const {
    state: { vaultId, answeredAlbums, unansweredAlbums },
    dispatch,
  } = useContext(VaultDetailContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoom, setZoom] = useState<ZoomLevel>('month');
  const [viewMode, setViewMode] = useState<ViewMode>('memories');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);

  const items: DatedItem[] = useMemo(() => {
    const all: { album: Album; isAnswered: boolean }[] = [
      ...excludeDefaultAlbum(answeredAlbums).map((album) => ({
        album,
        isAnswered: true,
      })),
      ...excludeDefaultAlbum(unansweredAlbums).map((album) => ({
        album,
        isAnswered: false,
      })),
    ];

    const datedItems: DatedItem[] = [];

    all.forEach(({ album, isAnswered }) => {
      const albumStart =
        parseDateSafe(album.startDate) ?? parseDateSafe(album.createdAt);
      if (!albumStart) return;

      const entries = memoriesOf(album)
        .map((content) => ({
          content,
          date:
            parseDateSafe(content.startDate) ??
            parseDateSafe(content.fileDate) ??
            parseDateSafe(content.createdAt) ??
            albumStart,
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      if (entries.length === 0) return;

      const explicitEnd = parseDateSafe(album.endDate);
      let end = explicitEnd ?? entries[entries.length - 1].date;
      if (end.getTime() < albumStart.getTime()) end = albumStart;

      datedItems.push({
        album,
        isAnswered,
        start: albumStart,
        end,
        entries,
      });
    });

    return datedItems.sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [answeredAlbums, unansweredAlbums]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      if (item.album.name?.toLowerCase().includes(q)) return true;
      if (item.album.location?.toLowerCase().includes(q)) return true;
      return item.entries.some((e) =>
        e.content.name?.toLowerCase().includes(q),
      );
    });
  }, [items, searchQuery]);

  type FlatMemory = {
    content: AlbumContentSerialized;
    date: Date;
  };

  // Memories mode searches memory titles only (no album-name fallback).
  const matchingMemories: FlatMemory[] = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const flat: FlatMemory[] = [];
    items.forEach((item) => {
      item.entries.forEach((e) => {
        if (q && !e.content.name?.toLowerCase().includes(q)) return;
        flat.push({ content: e.content, date: e.date });
      });
    });
    return flat.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [items, searchQuery]);

  // Collect only the periods that contain at least one dated item.
  // Albums mode uses album-level matches; memories mode uses memory matches.
  const activePeriods = useMemo(() => {
    const map = new Map<string, PeriodMeta>();
    if (viewMode === 'memories') {
      matchingMemories.forEach(({ date }) => {
        const meta = periodMeta(date, zoom);
        if (!map.has(meta.key)) map.set(meta.key, meta);
      });
    } else {
      filteredItems.forEach((item) => {
        const dates = [
          item.start,
          item.end,
          ...item.entries.map((e) => e.date),
        ];
        dates.forEach((d) => {
          const meta = periodMeta(d, zoom);
          if (!map.has(meta.key)) map.set(meta.key, meta);
        });
      });
    }
    return Array.from(map.values()).sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );
  }, [filteredItems, matchingMemories, zoom, viewMode]);

  // Group active periods by their parent group label (year), for the
  // banner row in month/week views.
  const groupedPeriods = useMemo(() => {
    if (zoom === 'year') return [] as { label: string; span: number }[];
    const groups: { label: string; span: number }[] = [];
    activePeriods.forEach((p) => {
      const label = p.groupLabel ?? '';
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.span += 1;
      } else {
        groups.push({ label, span: 1 });
      }
    });
    return groups;
  }, [activePeriods, zoom]);

  const isEmpty = items.length === 0;

  // The scroller's visible width, which the columns share out.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setAvailableWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isEmpty]);

  const columnWidth = Math.max(
    MIN_COLUMN_WIDTH[zoom],
    // Rounded down so the columns never add up to a hair wider than the
    // scroller, which would show a scrollbar for nothing.
    Math.floor((availableWidth / Math.max(activePeriods.length, 1)) * 100) /
      100,
  );

  // Map period key -> column index, then -> x position
  const periodIndex = useMemo(() => {
    const map = new Map<string, number>();
    activePeriods.forEach((p, i) => map.set(p.key, i));
    return map;
  }, [activePeriods]);

  const xForPeriodKey = (key: string) => {
    const idx = periodIndex.get(key) ?? 0;
    return idx * columnWidth;
  };

  // Each album spans exactly the columns from its start to its end, so it
  // never reaches into a period it doesn't cover. Albums that don't overlap
  // in time share a row: each goes into the first row that is free by its
  // start (albums arrive sorted by start date).
  const placedAlbums = useMemo(() => {
    const rowEnds: number[] = [];
    return filteredItems.map((item) => {
      const startCol = periodIndex.get(periodMeta(item.start, zoom).key) ?? 0;
      const endCol = Math.max(
        startCol,
        periodIndex.get(periodMeta(item.end, zoom).key) ?? startCol,
      );
      let row = rowEnds.findIndex((end) => end < startCol);
      if (row === -1) {
        row = rowEnds.length;
        rowEnds.push(endCol);
      } else {
        rowEnds[row] = endCol;
      }
      return { item, startCol, span: endCol - startCol + 1, row };
    });
  }, [filteredItems, periodIndex, zoom]);

  // How many entry chips fit in CHIP_ROWS rows of a card this many columns
  // wide. When some don't fit, the last slot says how many more there are.
  const visibleChipCount = (span: number, entryCount: number) => {
    const inner = span * columnWidth - CARD_INSET * 2 - CARD_CHROME;
    const perRow = Math.max(
      1,
      Math.floor((inner + CHIP_GAP) / (CHIP_MIN_WIDTH + CHIP_GAP)),
    );
    const room = perRow * CHIP_ROWS;
    return entryCount > room ? room - 1 : entryCount;
  };

  // Memories view: each column's memories, in date order.
  const memoriesByColumn = useMemo(() => {
    const columns = new Map<number, FlatMemory[]>();
    matchingMemories.forEach((memory) => {
      const col = periodIndex.get(periodMeta(memory.date, zoom).key) ?? 0;
      const list = columns.get(col) ?? [];
      list.push(memory);
      columns.set(col, list);
    });
    return columns;
  }, [matchingMemories, periodIndex, zoom]);

  // Cards stay inside their columns, so the columns are the whole track.
  const trackWidth = activePeriods.length * columnWidth;

  // Same destination as the dashboard's memory cards: the vault-wide memory
  // player, focused on this memory, with a breadcrumb back to the timeline.
  const openMemory = (contentId: number) =>
    goToPage(
      RouteName.MemoriesViewScreen,
      { id: vaultId },
      { content: contentId },
      withOrigin({ vaultId, tab: VaultNavigationTabs.timeline }),
    );

  const handleAlbumClick = (item: DatedItem) => {
    dispatch({
      type: vaultDetailActions.SET_ACTIVE_ALBUM,
      activeAlbumPointer: {
        id: item.album.id,
        listType: item.isAnswered ? 'answered' : 'unanswered',
      },
    });
  };

  const todayPeriodKey = periodMeta(new Date(), zoom).key;
  const showTodayLine = periodIndex.has(todayPeriodKey);
  const todayX = showTodayLine
    ? xForPeriodKey(todayPeriodKey) + columnWidth / 2
    : 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerControls}>
          <div className={styles.headerSearch}>
            <VaultSearchBar
              placeholder="Search timeline"
              onSearch={setSearchQuery}
            />
          </div>
          <div className={styles.modeToggle}>
            <button
              type="button"
              onClick={() => setViewMode('albums')}
              className={classnames(styles.modeButton, {
                [styles.modeButtonActive]: viewMode === 'albums',
              })}
            >
              <ColumnsIcon />
              Albums
            </button>
            <button
              type="button"
              onClick={() => setViewMode('memories')}
              className={classnames(styles.modeButton, {
                [styles.modeButtonActive]: viewMode === 'memories',
              })}
            >
              <GridIcon />
              Memories
            </button>
          </div>
          <div className={styles.zoomGroup}>
            {(['year', 'month', 'week'] as ZoomLevel[]).map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => setZoom(z)}
                className={classnames(styles.zoomButton, {
                  [styles.zoomButtonActive]: zoom === z,
                })}
              >
                {z[0].toUpperCase() + z.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <ClockIcon />
          </div>
          <span className={styles.emptyTitle}>No dated albums yet</span>
          <span className={styles.emptySubtitle}>
            Add a date to your albums to see them on the timeline.
          </span>
        </div>
      ) : (
        <div className={styles.timelineWrap}>
          <div ref={scrollerRef} className={styles.scroller}>
            <div className={styles.track} style={{ width: trackWidth }}>
              {/* Axis */}
              <div className={styles.axis}>
                {zoom !== 'year' && groupedPeriods.length > 0 && (
                  <div className={styles.axisGroupRow}>
                    {groupedPeriods.map((group) => (
                      <div
                        key={group.label}
                        className={styles.axisGroup}
                        style={{ width: group.span * columnWidth }}
                      >
                        <span className={styles.axisGroupLabel}>
                          {group.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.axisCellRow}>
                  {activePeriods.map((p) => (
                    <div
                      key={p.key}
                      className={classnames(styles.axisCell, {
                        [styles.axisCellMajor]: zoom === 'year',
                      })}
                      style={{ width: columnWidth }}
                    >
                      <span className={styles.axisCellLabel}>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column gridlines */}
              <div className={styles.gridLayer} aria-hidden>
                {activePeriods.map((p, idx) => (
                  <div
                    key={`grid-${p.key}`}
                    className={classnames(styles.gridline, {
                      [styles.gridlineMajor]:
                        zoom === 'year' || p.date.getMonth() === 0,
                    })}
                    style={{ left: idx * columnWidth }}
                  />
                ))}
              </div>

              {/* Today marker */}
              {showTodayLine && (
                <div
                  className={styles.todayLine}
                  style={{ left: todayX }}
                  aria-hidden
                >
                  <span className={styles.todayLabel}>Today</span>
                </div>
              )}

              {/* Lanes */}
              <div className={styles.viewSwitcher} key={viewMode}>
                {viewMode === 'memories' ? (
                  <div
                    className={classnames(
                      styles.memoryColumns,
                      styles.viewLayer,
                    )}
                    style={{
                      gridTemplateColumns: `repeat(${activePeriods.length}, ${columnWidth}px)`,
                    }}
                  >
                    {activePeriods.map((period, col) => (
                      <div key={period.key} className={styles.memoryColumn}>
                        {(memoriesByColumn.get(col) ?? []).map(
                          ({ content, date }) => (
                            <WhenNearView
                              key={content.id}
                              placeholderClassName={styles.memoryPlaceholder}
                            >
                              <MemoryCard
                                content={content}
                                dateLabel={formatLongDate(date)}
                                onClick={() => openMemory(content.id)}
                              />
                            </WhenNearView>
                          ),
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={classnames(styles.lanes, styles.viewLayer)}
                    style={{
                      gridTemplateColumns: `repeat(${activePeriods.length}, ${columnWidth}px)`,
                    }}
                  >
                    {placedAlbums.map(({ item, startCol, span, row }) => {
                      const rangeLabel =
                        item.start.getTime() === item.end.getTime()
                          ? formatShortDate(item.start)
                          : `${formatShortDate(item.start)} – ${formatShortDate(
                              item.end,
                            )}`;
                      const isHovered = hoveredId === item.album.id;
                      const entryCount = item.entries.length;
                      const shownCount = visibleChipCount(span, entryCount);
                      const moreCount = entryCount - shownCount;
                      return (
                        <button
                          key={item.album.id}
                          type="button"
                          className={classnames(styles.albumBar, {
                            [styles.albumBarAnswered]: item.isAnswered,
                            [styles.albumBarUnanswered]: !item.isAnswered,
                            [styles.albumBarHovered]: isHovered,
                          })}
                          style={{
                            gridColumn: `${startCol + 1} / span ${span}`,
                            gridRow: row + 1,
                          }}
                          onClick={() => handleAlbumClick(item)}
                          onMouseEnter={() => setHoveredId(item.album.id)}
                          onMouseLeave={() => setHoveredId(null)}
                        >
                          <div className={styles.albumBarGlow} aria-hidden />
                          <div className={styles.albumBarInner}>
                            <div className={styles.albumHeaderRow}>
                              <div className={styles.albumThumb}>
                                <AlbumThumbnail
                                  album={item.album}
                                  alt={item.album.name}
                                  className={styles.albumThumbImg}
                                  fallback={
                                    <div className={styles.albumThumbFallback}>
                                      {getAlbumRepresentativeIcon(item.album)}
                                    </div>
                                  }
                                />
                              </div>
                              <div className={styles.albumMeta}>
                                <span className={styles.albumName}>
                                  {item.album.name || 'Untitled album'}
                                </span>
                                <span className={styles.albumDates}>
                                  {rangeLabel}
                                  {!parseDateSafe(item.album.endDate) &&
                                    item.entries.length > 0 && (
                                      <span className={styles.inferred}>
                                        {' · inferred end'}
                                      </span>
                                    )}
                                </span>
                              </div>
                              <div className={styles.entryCountBadge}>
                                <span className={styles.entryCountNumber}>
                                  {entryCount}
                                </span>
                                <span className={styles.entryCountLabel}>
                                  {entryCount === 1 ? 'memory' : 'memories'}
                                </span>
                              </div>
                            </div>

                            {entryCount > 0 && (
                              <div className={styles.entriesRow}>
                                {item.entries
                                  .slice(0, shownCount)
                                  .map((entry) => {
                                    const type = adaptContentType(
                                      entry.content,
                                    );
                                    return (
                                      <div
                                        key={entry.content.id}
                                        className={styles.entryChip}
                                      >
                                        <div className={styles.entryChipIcon}>
                                          {ContentIcons[type]}
                                        </div>
                                        <div className={styles.entryChipBody}>
                                          <span
                                            className={styles.entryChipName}
                                          >
                                            {entry.content.name ||
                                              entry.content.description ||
                                              'Memory'}
                                          </span>
                                          <span
                                            className={styles.entryChipDate}
                                          >
                                            {formatShortDate(entry.date)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                {moreCount > 0 && (
                                  <div
                                    className={classnames(
                                      styles.entryChip,
                                      styles.entryChipMore,
                                    )}
                                  >
                                    {`+${moreCount} more`}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
